/**
 * What a contract day earned.
 *
 * The Daily Report's revenue is m³ × a mix's sell price. A contract day's is
 * equipment days and operator days × their charge-out rates, plus a flat fee
 * when the day was cancelled. Same shape, different multiplicands.
 *
 * A day count comes from dates, not a typed number. Equipment (and a hire)
 * bills for `daysInclusive(start, end)` — its own row's dates, inclusive at
 * both ends, falling back to the card's `report_date`/`end_date` when the
 * row's own dates are blank. `contract_report_equipment.days` is retired:
 * the column stays in the database, this module stops reading it.
 *
 * A cancelled day bills the fee BECAUSE the crew was stood down. Equipment
 * and hires still bill — the machine was on site either way — but operators
 * bill nothing: billing their days on top would charge twice for the same
 * non-work. See the `cancelled` branch below.
 *
 * This module knows nothing about COST, with one deliberate exception: a
 * hire's `cost_per_day` — what SCI PAYS a third party — which is priced here
 * only because it lives on the same row as the dates that size it, and lands
 * in `summary.hires_cost`, never in `summary.revenue`. An operator's
 * charge-out rate and the wage they are paid are different numbers that
 * happen to hang off the same name, and the surest way to confuse them is to
 * compute both here. Wage cost stays in calc/pnl.ts where wages already
 * live — including the cancelled-day suppression on the wage side, which
 * this module cannot reach and does not attempt.
 *
 * A name with no rate yields null revenue and a reason, never 0 — a day billed
 * at zero looks settled, which is a lie a reader cannot see through. A hire's
 * price is typed on the row rather than looked up in a rate table, so it is
 * never "no rate" — but it is unpriced in two other ways, both flagged the
 * same standing way. Its `cost_per_day` can be 0, which storage cannot tell
 * apart from "not yet priced" and which no real third party charges (Task 4
 * re-review, N3). And it can carry a broken date range, which gets the
 * identical treatment as of the Task 4 review (I4): a named equipment or hire row
 * whose `start_date`/`end_date` (its own, or the card's it fell back to)
 * do not resolve to a real span — reversed, malformed, or simply invalid —
 * is flagged unpriced with a reason (`CONTRACT_UNPRICED.badDateRange`),
 * never silently dropped. Dropping it used to make the row, and its whole
 * revenue or cost, vanish with no signal anywhere — the exact "billed at
 * zero" lie this module exists to prevent, arriving by the one path that
 * bypassed `CONTRACT_UNPRICED`. A row with a genuinely BLANK name is a
 * different thing — a spare line the crew left alone — and is still
 * skipped without a flag, not treated as an error.
 */
import { foldName, priceInForce } from './pnl';
import type {
  CancellationFee,
  ContractReport,
  ContractReportEquipment,
  ContractReportHire,
  ContractReportOperator,
  EquipmentRate,
  OperatorRate,
  RateUnit
} from '../types';

/** Every contract rate row, as the routes load them. */
export interface ContractRates {
  equipment: readonly EquipmentRate[];
  operators: readonly OperatorRate[];
  cancellation: readonly CancellationFee[];
}

/**
 * One billed line: what it was, how many days, and what that earned. Self-
 * stamped with the report it came from — `report_id`/`report_date`/
 * `ticket_no` — the same convention `PnlLabourLine`/`PnlMaterialLine`/
 * `PnlPourLine` already follow in calc/pnl.ts, so a line pulled out of its
 * report (onto the admin project breakdown, alongside lines from other
 * reports) can still say which one it came from.
 */
export interface ContractLine {
  report_id: number;
  report_date: string;
  ticket_no: string;
  label: string;
  days: number;
  /**
   * The calendar range this line's SPAN — `daysInclusive(start_date,
   * end_date)` — covers, NOT necessarily how many days it BILLS. That is
   * `days`, above, and the two are the same number for one kind of line and
   * different for the other:
   *
   * - Equipment and a hire row's own (or the card's, on fallback) dates:
   *   `days` IS `daysInclusive(start_date, end_date)` — `rangedRows`
   *   derives one from the other, so they can never disagree, and a
   *   consumer CAN split `revenue` evenly across `days`, one per calendar
   *   day, because each calendar day in the span really is a separately
   *   billed day.
   * - An operator or the cancellation fee: `start_date === end_date ===
   *   report_date`, ALWAYS a one-day span, whatever `days` says. `days`
   *   there is a typed quantity (an operator entered as 2 days) with no
   *   date range of its own to split across — the one calendar day is when
   *   the card was filed, not "the first of two days billed". Splitting
   *   `revenue` evenly across `days` per calendar day for one of these
   *   lines is wrong: it is exactly the bug a consumer reading this
   *   comment's OLD wording produced (Task 3 review round 1, Critical 1) —
   *   a 2-day operator billed as if 1 calendar day carried the whole
   *   line's money divided in half.
   *
   * A consumer must know which kind of line it is holding before treating
   * `days` and the date span as interchangeable; `admin-pnl.ts`'s
   * `lineSpan`/`scopedLineShare` are the reference implementation of that
   * distinction.
   */
  start_date: string;
  end_date: string;
  /**
   * Which of the machine's rates this line billed on, and how many of that
   * unit it billed. For an operator, a hire or the cancellation fee this is
   * always `'day'` and `billed_units === days` — those bill by the day and
   * nothing else. For equipment it is the row's chosen `rate_unit`, and
   * `billed_units` is the day span converted UP to whole units of it
   * (`ceil(days/7)` weeks, `ceil(days/30)` months) — so a reader can show
   * "2 wk × $500" rather than mislabelling the calendar-day span as the amount
   * billed. `rate` is per THIS unit, and `revenue === billed_units × rate`.
   */
  rate_unit: RateUnit;
  billed_units: number;
  /** Null when it could not be priced — see `reason`. `rate` is per `rate_unit`. */
  rate: number | null;
  revenue: number | null;
  reason: string | null;
}

export interface ContractSummary {
  revenue: number;
  equipment_revenue: number;
  operator_revenue: number;
  cancellation: number;
  /**
   * What SCI PAYS for everything hired in, over the card. Deliberately
   * excluded from `revenue` — a hire's cost never lands in what SCI earned.
   */
  hires_cost: number;
  unpriced_count: number;
}

export interface ContractPnl {
  report_id: number;
  report_date: string;
  site_id: number | null;
  equipment: ContractLine[];
  operators: ContractLine[];
  /** What was hired in, priced as COST (`hires_cost`), never revenue. */
  hires: ContractLine[];
  /**
   * 0 or 1 line: the day's cancellation fee, only when `cancelled = 1`. A
   * `ContractLine` same as the others — `label` is always 'Cancellation
   * fee', `days` always 1 — so it prices and displays through the identical
   * machinery rather than a third, bespoke shape for one flat fee.
   */
  cancellation: ContractLine[];
  summary: ContractSummary;
}

export interface ContractReportInput {
  contract_report: ContractReport;
  equipment: readonly ContractReportEquipment[];
  operators: readonly ContractReportOperator[];
  /**
   * Optional so a caller with nothing to say about hires still compiles —
   * an absent list prices no hires, same as an empty one. `admin-pnl.ts`'s
   * `loadContractReports` supplies it (Task 4 review, C2); a test fixture
   * that omits it entirely still behaves exactly as it did before hires
   * existed.
   */
  hires?: readonly ContractReportHire[];
}

/** Words for the unit a row bills on — used in the "no weekly rate" reason. */
const UNIT_WORD: Record<RateUnit, string> = { day: 'daily', week: 'weekly', month: 'monthly' };

/** The words on screen for a contract line that could not be priced. */
export const CONTRACT_UNPRICED = {
  noRate: (name: string) => `no rate for "${name}"`,
  /**
   * Equipment picked a unit the machine has no rate for. Distinct wording from
   * `noRate` so admin knows the machine IS priced, just not at that unit — the
   * fix is a weekly/monthly figure on the Rates screen, or switching the row
   * back to the day it does have.
   */
  noUnitRate: (name: string, unit: RateUnit) => `no ${UNIT_WORD[unit]} rate for "${name}"`,
  noCancellationFee: 'no cancellation fee set',
  badDateRange: (name: string) => `no valid date range for "${name}"`,
  /**
   * A hire the crew recorded that admin has not put a cost on. Its own
   * wording rather than `noRate`'s: a hire has no rate TABLE to be missing
   * from — the figure is typed on the row itself — so "no rate for X" would
   * send a reader looking for a rate nobody was ever meant to set.
   */
  noHireCost: (name: string) => `no cost set for "${name}"`
} as const;

function money(value: number): number {
  return Math.round(value * 100) / 100;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How many days make one billed week and one billed month. A span is charged
 * UP to whole units of the row's chosen rate (owner's decision): 10 days at a
 * weekly rate is `ceil(10/7) = 2` weeks, the way a rental yard charges any part
 * of a week as a full one. A row on the day rate bills each calendar day, so
 * `day` needs no divisor.
 */
const UNIT_DAYS: Record<Exclude<RateUnit, 'day'>, number> = { week: 7, month: 30 };

/** Whole units of `unit` a `days`-long span bills, rounded up. */
function unitsFor(days: number, unit: RateUnit): number {
  if (unit === 'day') return days;
  return Math.ceil(days / UNIT_DAYS[unit]);
}

/**
 * Parse a strict `YYYY-MM-DD` string as a UTC midnight timestamp. Uses
 * `Date.UTC` rather than `new Date(string)` — the latter is interpreted in
 * the local timezone for a date-only ISO string in some engines and would
 * shift a range's length depending on where the Worker happens to run.
 * Returns null for anything that is not exactly that shape, AND for anything
 * that is that shape but is not a date.
 *
 * The round-trip is the whole guard (Task 4 re-review, N2). `Date.UTC` does
 * not reject a month or a day out of range, it ROLLS OVER: `Date.UTC(2026,
 * 12, 1)` is 2027-01-01 and `Date.UTC(2026, 1, 30)` is 2026-03-02, neither
 * of them NaN. A single mistyped digit — `end_date: '2026-13-01'` — was
 * therefore accepted and billed 130 days across five months with nothing
 * flagged. So the parsed instant must read back as exactly the year, month
 * and day it was handed, or it is not a date. (This also catches the
 * two-digit-year trap on the way past: `Date.UTC(26, 0, 1)` means 1926.)
 */
function parseIsoDate(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, mo, d] = match;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const ms = Date.UTC(year, month - 1, day);
  if (Number.isNaN(ms)) return null;
  const back = new Date(ms);
  const sameDate =
    back.getUTCFullYear() === year && back.getUTCMonth() === month - 1 && back.getUTCDate() === day;
  return sameDate ? ms : null;
}

/**
 * Is this string a real calendar date? The ONE date rule this app has —
 * `parseIsoDate` above, shape and calendar together — exposed so the write
 * path can refuse at the door exactly what this module would otherwise have
 * to flag after the fact (`quality-contract-reports.ts`'s
 * `checkCardDateRange`). A second, shape-only copy of the rule out there is
 * how `'2026-13-01'` got a 200 in the first place.
 */
export function isCalendarDate(value: string): boolean {
  return parseIsoDate(value) !== null;
}

/**
 * The number of whole calendar days from `start` to `end`, inclusive at both
 * ends — 1st to 20th is 20 days. Null when either date is blank or invalid,
 * or when `end` precedes `start`. Whole days only: there is no half-day and
 * no decimal override anywhere in this module.
 */
export function daysInclusive(start: string, end: string): number | null {
  const startMs = parseIsoDate(start);
  const endMs = parseIsoDate(end);
  if (startMs === null || endMs === null) return null;
  const days = Math.round((endMs - startMs) / MS_PER_DAY) + 1;
  return days >= 1 ? days : null;
}

/**
 * A row's own dates, falling back to the card's own range — `cardStart`/
 * `cardEnd` — when either is blank. This is the ONE rule in the whole app
 * for what date span an equipment or hire row bills against, and it used to
 * exist in two places: here, and a hand-copied three-line version inside
 * `ContractReportForm.svelte`'s own `rowDayCount`, kept in step only by
 * both sides calling this module's `daysInclusive` and by nobody editing
 * one without the other (Task 5 review, scrutiny point 3 / I3 / M1 — the
 * reviewer measured ten cases and found no live disagreement, but nothing
 * PINNED the two copies together). Exported and reused by both `rangedRows`
 * below and the form, so there is now exactly one copy to test and no way
 * for the two to drift.
 *
 * Returns the RESOLVED `start`/`end` alongside `days` — not just the count —
 * because `rangedRows` needs the resolved pair too (for `ContractLine`'s own
 * `start_date`/`end_date`, see that type's comment), and computing it twice
 * from the same three lines is the exact duplication this function exists to
 * remove.
 */
export function resolveRowRange(
  rowStart: string,
  rowEnd: string,
  cardStart: string,
  cardEnd: string
): { start: string; end: string; days: number | null } {
  const start = rowStart || cardStart;
  const end = rowEnd || cardEnd;
  return { start, end, days: daysInclusive(start, end) };
}

/** The rate in force for a folded name, or null. */
function rateFor<T extends { id: number; effective_from: string }>(
  rows: readonly T[],
  nameOf: (row: T) => string,
  name: string,
  onDate: string
): T | null {
  const key = foldName(name);
  if (key === '') return null;
  const mine = rows.filter((row) => foldName(nameOf(row)) === key);
  return mine.length === 0 ? null : priceInForce(mine, onDate);
}

/**
 * `reason` is the words for a null `rate`. It defaults to "no rate for X" —
 * the equipment/operator case, where a rate was looked up in a table and
 * none was in force — and a hire overrides it, because a hire's price is
 * typed on its own row rather than looked up, so "no rate" would name a
 * table that does not exist for it (`CONTRACT_UNPRICED.noHireCost`).
 */
function line(
  stamp: { report_id: number; report_date: string; ticket_no: string },
  label: string,
  days: number,
  rate: number | null,
  start_date: string,
  end_date: string,
  reason: string = CONTRACT_UNPRICED.noRate(label.trim()),
  // Defaults keep every day-billed caller (operators, hires, cancellation)
  // exactly as it was: bill each calendar day at the day rate. Equipment
  // overrides these to bill its chosen unit — `rate` is then per that unit and
  // `billed_units` the span rounded up to whole units of it.
  rate_unit: RateUnit = 'day',
  billed_units: number = days
): ContractLine {
  if (rate === null) {
    return { ...stamp, label, days, start_date, end_date, rate_unit, billed_units, rate: null, revenue: null, reason };
  }
  return { ...stamp, label, days, start_date, end_date, rate_unit, billed_units, rate, revenue: money(billed_units * rate), reason: null };
}

/**
 * A named equipment or hire row whose date range (its own, or the card's it
 * fell back to) does not resolve to a real span — reversed, malformed, or
 * simply invalid. Attached to the CARD's own single `report_date`, the same
 * one-day attachment an operator or the cancellation fee already uses,
 * because there is no other date this line can safely be said to belong to
 * (`days: 1` matches that one-day span exactly, so `admin-pnl.ts`'s
 * `lineSpan`/`scopedLineShare` treat it the same all-or-nothing way an
 * operator line already is, rather than falling into their defensive
 * prorate branch). Flagged with a reason, never dropped (Task 4 review,
 * I4) — the standing rule that a line nobody could price yields no money
 * and a reason, never a silent 0, now covers a broken range exactly the way
 * it already covers a missing rate.
 *
 * All of which holds only while the card's own `report_date` IS a date. When
 * THAT is the broken one — the only date on the card nothing used to
 * validate — stamping it here left the line claiming `days: 1` over a span
 * of zero, and `admin-pnl.ts`'s `scopedLineShare` drops a line with no span,
 * taking the flag down with the money (Task 4 re-review, N4). So an
 * unusable `report_date` yields no date and no days at all rather than a
 * fictional one-day range: `lineSpan` reads 0 either way, but now it reads 0
 * because the line honestly has no date, which is a thing a consumer can
 * see and act on.
 */
function badRangeLine(
  stamp: { report_id: number; report_date: string; ticket_no: string },
  label: string
): ContractLine {
  const dated = parseIsoDate(stamp.report_date) !== null;
  const days = dated ? 1 : 0;
  return {
    ...stamp,
    label,
    days,
    start_date: dated ? stamp.report_date : '',
    end_date: dated ? stamp.report_date : '',
    // A line nobody could size has no meaningful unit; report the day span it
    // was stamped with, so `billed_units` never contradicts `days`.
    rate_unit: 'day',
    billed_units: days,
    rate: null,
    revenue: null,
    reason: CONTRACT_UNPRICED.badDateRange(label.trim())
  };
}

/**
 * Every row with a real name — falling back to `fallbackStart`/`fallbackEnd`
 * (the card's own range) when the row's own dates are blank — paired with
 * whatever day count its range resolves to, or `null` when that range is
 * invalid (reversed, malformed, or blank on both the row and its fallback).
 * A blank name is a different thing, an empty row the crew left alone, and
 * is skipped here rather than returned at all: it is never a charge anybody
 * failed to price, so it must not show up as one downstream either. A `null`
 * days row IS returned — unlike a blank name, a broken range on a named row
 * is exactly the kind of thing `CONTRACT_UNPRICED` exists to flag (see
 * `badRangeLine`, used by every caller of this function).
 */
function rangedRows<T>(
  rows: readonly T[],
  name: (row: T) => string,
  dates: (row: T) => { start: string; end: string },
  fallbackStart: string,
  fallbackEnd: string
): Array<{ row: T; start: string; end: string; days: number | null }> {
  const out: Array<{ row: T; start: string; end: string; days: number | null }> = [];
  for (const row of rows) {
    if (foldName(name(row)) === '') continue;
    const { start: rowStart, end: rowEnd } = dates(row);
    out.push({ row, ...resolveRowRange(rowStart, rowEnd, fallbackStart, fallbackEnd) });
  }
  return out;
}

export function priceContractReport(
  input: ContractReportInput,
  rates: ContractRates
): ContractPnl {
  const report = input.contract_report;
  const onDate = report.report_date;
  const stamp = { report_id: report.id, report_date: onDate, ticket_no: report.ticket_no };

  // The card's own range. A blank end_date means a card written before
  // ranges existed (or never given one) — treated as a single day on its
  // report_date, exactly how it billed before this task, so no backfill is
  // needed for an existing report to keep working.
  const cardStart = report.report_date;
  const cardEnd = report.end_date || report.report_date;

  const equipment = rangedRows(
    input.equipment,
    (r) => r.equipment,
    (r) => ({ start: r.start_date ?? '', end: r.end_date ?? '' }),
    cardStart,
    cardEnd
  ).map(({ row, start, end, days }) => {
    if (days === null) return badRangeLine(stamp, row.equipment);
    const unit: RateUnit = row.rate_unit ?? 'day';
    const rate = rateFor(rates.equipment, (r) => r.equipment, row.equipment, onDate);
    // The per-unit figure for the row's chosen unit. A machine priced day-only
    // has null week/month rates: a row on that unit is unpriced (its own
    // reason), never billed at a coerced 0 — the same standing rule a missing
    // day rate already gets, worded so admin knows WHICH unit to price.
    const perUnit =
      rate === null
        ? null
        : unit === 'week'
          ? rate.charge_per_week
          : unit === 'month'
            ? rate.charge_per_month
            : rate.charge_per_day;
    const reason =
      rate === null
        ? CONTRACT_UNPRICED.noRate(row.equipment.trim())
        : CONTRACT_UNPRICED.noUnitRate(row.equipment.trim(), unit);
    return line(stamp, row.equipment, days, perUnit ?? null, start, end, reason, unit, unitsFor(days, unit));
  });

  // A row with no name or no days is an empty row the crew left alone, not a
  // charge anybody failed to price. It is skipped, not flagged.
  const billable = <T>(rows: readonly T[], name: (r: T) => string, days: (r: T) => number) =>
    rows.filter((row) => foldName(name(row)) !== '' && days(row) > 0);

  // A cancelled day bills the fee BECAUSE the crew was stood down. Billing
  // their days on top charges twice for the same non-work, and costing their
  // wages pays for hours nobody worked. Equipment and hires still bill — the
  // machine was on site either way. Operators have no date range of their
  // own (spec: "Operators are untouched"), so each line lands on the report
  // date alone, same as it always has.
  const operators = report.cancelled === 1
    ? []
    : billable(
        input.operators,
        (r) => r.name,
        (r) => r.days
      ).map((row) => {
        const rate = rateFor(rates.operators, (r) => r.name, row.name, onDate);
        return line(stamp, row.name, row.days, rate ? rate.charge_per_day : null, onDate, onDate);
      });

  // A hire's price is typed on the row (`cost_per_day`), not looked up in a
  // rate table — so it is unbillable when its name is blank (skipped, same
  // rule as equipment), flagged when its date range is broken
  // (`badRangeLine`, same rule as equipment — Task 4 review, I4), and
  // flagged again when nobody has put a cost on it at all (Task 4
  // re-review, N3).
  //
  // That last one needs saying plainly, because it reads as arithmetic and
  // is not: `cost_per_day` is `NOT NULL DEFAULT 0`, so a hire the crew
  // recorded and admin has not priced yet is INDISTINGUISHABLE IN STORAGE
  // from one deliberately costed at zero — and a third party on site
  // billing nothing is not a real case. A 0 is therefore read as "no price
  // yet", which is this module's standing rule (`rate === null` yields a
  // reason, never a settled-looking $0) applied on the cost side. Read as a
  // real zero instead, it understates COST, which OVERSTATES margin by the
  // entire missing figure with nothing on screen to say so — the same lie
  // as a $0 revenue line, told in the direction that flatters.
  //
  // This is COST: what SCI PAYS, never what it bills. It reuses
  // ContractLine's `revenue` field (same shape as equipment/operators) but
  // is summed into `summary.hires_cost`, never `summary.revenue` — see that
  // assembly below.
  const hires = rangedRows(
    input.hires ?? [],
    (r) => r.name,
    (r) => ({ start: r.start_date ?? '', end: r.end_date ?? '' }),
    cardStart,
    cardEnd
  ).map(({ row, start, end, days }) =>
    days === null
      ? badRangeLine(stamp, row.name)
      : line(
          stamp,
          row.name,
          days,
          row.cost_per_day > 0 ? row.cost_per_day : null,
          start,
          end,
          CONTRACT_UNPRICED.noHireCost(row.name.trim())
        )
  );

  let cancellation = 0;
  let cancellationUnpriced = 0;
  const cancellationLines: ContractLine[] = [];
  if (report.cancelled === 1) {
    const fee = priceInForce(rates.cancellation, onDate);
    if (fee === null) {
      cancellationUnpriced = 1;
      cancellationLines.push({
        ...stamp, label: 'Cancellation fee', days: 1, start_date: onDate, end_date: onDate,
        rate_unit: 'day', billed_units: 1, rate: null, revenue: null, reason: CONTRACT_UNPRICED.noCancellationFee
      });
    } else {
      cancellation = money(fee.amount);
      cancellationLines.push({
        ...stamp, label: 'Cancellation fee', days: 1, start_date: onDate, end_date: onDate,
        rate_unit: 'day', billed_units: 1, rate: fee.amount, revenue: cancellation, reason: null
      });
    }
  }

  const sum = (lines: ContractLine[]) => money(lines.reduce((t, l) => t + (l.revenue ?? 0), 0));
  const equipment_revenue = sum(equipment);
  const operator_revenue = sum(operators);
  const hires_cost = sum(hires);

  return {
    report_id: report.id,
    report_date: onDate,
    site_id: report.site_id,
    equipment,
    operators,
    hires,
    cancellation: cancellationLines,
    summary: {
      // hires_cost deliberately excluded — a hire's cost is what SCI PAYS,
      // never what it earned.
      revenue: money(equipment_revenue + operator_revenue + cancellation),
      equipment_revenue,
      operator_revenue,
      cancellation,
      hires_cost,
      // A hire's `reason` is set two ways — a broken date range (Task 4
      // review, I4) and, since the re-review's N3, a `cost_per_day` nobody
      // has set (`noHireCost`, sixty lines above). Never a missing RATE: a
      // hire has no rate table to be missing from. Either way it is real
      // money — cost, not revenue — going unflagged the same way an
      // equipment row's would, so it counts here too.
      unpriced_count:
        equipment.filter((l) => l.reason !== null).length +
        operators.filter((l) => l.reason !== null).length +
        hires.filter((l) => l.reason !== null).length +
        cancellationUnpriced
    }
  };
}
